import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, X, Trophy, ArrowRight, RotateCcw, Sparkles, Star } from "lucide-react";

import { Navbar } from "@/components/layout/Nav";
import { useLang } from "@/context/LangContext";
import { GAMES } from "@/constants/testIds";
import { Carlos, Ana } from "@/components/characters/Characters";

/* ------------------------ shared UI helpers ------------------------ */
const ScoreChip = ({ score, total, testid }) => (
  <div
    data-testid={testid}
    className="inline-flex items-center gap-1 brutal-sm px-3 py-1 bg-canales-yellow font-display font-black text-sm"
  >
    <Star className="w-4 h-4" /> {score} / {total}
  </div>
);

const GameHeader = ({ title, subtitle, score, total, testid }) => (
  <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
    <div>
      <h3 className="font-display font-black text-2xl">{title}</h3>
      <p className="font-body text-sm text-neutral-700">{subtitle}</p>
    </div>
    <ScoreChip score={score} total={total} testid={testid} />
  </div>
);

/* ============================================================
   GAME 1 — Order the steps (multi-round: 3 flows)
============================================================ */
const ORDER_ROUNDS = {
  es: [
    {
      title: "Proceso de VENTA",
      steps: ["Preparación", "GREET", "DISCOVER", "DEMONSTRATE", "CLOSE", "FOLLOW-UP"],
    },
    {
      title: "Manejo de QUEJA",
      steps: ["Escuchar sin interrumpir", "Disculpa sincera", "Ofrecer solución", "Confirmar con el cliente", "Follow-up en 48h"],
    },
    {
      title: "Cierre con FINANCIAMIENTO",
      steps: ["Detectar duda por precio", "Traducir a pago mensual", "Comparar con gasto diario", "Correr pre-check", "Cierre alternativo"],
    },
  ],
  en: [
    {
      title: "SALES process",
      steps: ["Preparation", "GREET", "DISCOVER", "DEMONSTRATE", "CLOSE", "FOLLOW-UP"],
    },
    {
      title: "COMPLAINT handling",
      steps: ["Listen without interrupting", "Sincere apology", "Offer a solution", "Confirm with the customer", "Follow-up in 48h"],
    },
    {
      title: "Closing with FINANCING",
      steps: ["Spot price doubt", "Translate to monthly payment", "Compare to daily spend", "Run pre-check", "Alternative close"],
    },
  ],
};

const OrderGame = ({ lang, t }) => {
  const rounds = ORDER_ROUNDS[lang];
  const [round, setRound] = useState(0);
  const [score, setScore] = useState(0);
  const correct = rounds[round].steps;
  const [pool, setPool] = useState(() => [...correct].sort(() => Math.random() - 0.5));
  const [placed, setPlaced] = useState([]);
  const [done, setDone] = useState(false);

  const place = (label) => {
    setPlaced((p) => [...p, label]);
    setPool((s) => s.filter((x) => x !== label));
  };
  const reset = () => {
    setPool([...correct].sort(() => Math.random() - 0.5));
    setPlaced([]);
    setDone(false);
  };
  const check = () => {
    setDone(true);
    if (placed.length === correct.length && placed.every((v, i) => v === correct[i])) {
      setScore((s) => s + 1);
    }
  };
  const nextRound = () => {
    const nr = (round + 1) % rounds.length;
    setRound(nr);
    setPool([...rounds[nr].steps].sort(() => Math.random() - 0.5));
    setPlaced([]);
    setDone(false);
  };

  const isCorrect = placed.length === correct.length && placed.every((v, i) => v === correct[i]);

  return (
    <div className="brutal p-6 bg-white" data-testid="game-order">
      <GameHeader
        title={t("gameOrderTitle")}
        subtitle={rounds[round].title}
        score={score}
        total={rounds.length}
        testid="game-order-score"
      />
      <p className="font-body text-xs text-neutral-600 mb-3">
        {lang === "es" ? "Toca los pasos en el orden correcto." : "Tap the steps in the correct order."}
      </p>
      <div className="flex flex-wrap gap-2 min-h-[3rem] mb-3">
        {placed.map((p, i) => (
          <div key={i} className="brutal-sm px-3 py-1 bg-canales-blue text-white font-display font-bold text-sm">
            {i + 1}. {p}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 border-2 border-dashed border-black rounded-2xl p-3">
        {pool.map((p, i) => (
          <button
            key={p}
            data-testid={GAMES.option(i)}
            onClick={() => place(p)}
            className="brutal-btn bg-canales-yellow px-3 py-1 text-sm"
          >
            {p}
          </button>
        ))}
      </div>
      <div className="mt-4 flex gap-2 flex-wrap">
        <button onClick={check} data-testid={GAMES.submit} className="brutal-btn bg-canales-red text-white px-4 py-2">
          {t("submit")}
        </button>
        <button onClick={reset} className="brutal-btn bg-white px-4 py-2 flex items-center gap-2">
          <RotateCcw className="w-4 h-4" /> Reset
        </button>
        <button
          onClick={nextRound}
          data-testid="game-order-next-round"
          className="brutal-btn bg-canales-blue text-white px-4 py-2 flex items-center gap-2"
        >
          {lang === "es" ? "Siguiente ronda" : "Next round"} <ArrowRight className="w-4 h-4" />
        </button>
      </div>
      <AnimatePresence>
        {done && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mt-4 brutal p-4 ${isCorrect ? "bg-green-100" : "bg-red-100"} flex items-center gap-2 font-display font-bold`}
          >
            {isCorrect ? <Check className="text-green-700" /> : <X className="text-red-700" />}
            {isCorrect ? t("correct") : t("incorrect")}
            {!isCorrect && (
              <span className="text-sm font-body">
                {lang === "es" ? "Correcto: " : "Correct: "}
                {correct.join(" → ")}
              </span>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ============================================================
   GAME 2 — F.A.B. Matcher (12 items, 6 per round)
============================================================ */
const FAB_POOL = {
  es: [
    { text: "Cojines de espuma memory", type: "F" },
    { text: "Se adapta a tu cuerpo", type: "A" },
    { text: "Descansarás profundo cada noche", type: "B" },
    { text: "Tela impermeable", type: "F" },
    { text: "No se mancha con jugo o café", type: "A" },
    { text: "Tu casa siempre lucirá nueva", type: "B" },
    { text: "10 años de garantía", type: "F" },
    { text: "No pagarás reparaciones", type: "A" },
    { text: "Tranquilidad para tu familia", type: "B" },
    { text: "Base ajustable eléctrica", type: "F" },
    { text: "Puedes leer o ver TV cómodo", type: "A" },
    { text: "Tus noches serán tu premio del día", type: "B" },
  ],
  en: [
    { text: "Memory foam cushions", type: "F" },
    { text: "Adapts to your body", type: "A" },
    { text: "You'll sleep deeply every night", type: "B" },
    { text: "Waterproof fabric", type: "F" },
    { text: "Doesn't stain from juice or coffee", type: "A" },
    { text: "Your home always looks new", type: "B" },
    { text: "10-year warranty", type: "F" },
    { text: "You won't pay for repairs", type: "A" },
    { text: "Peace of mind for your family", type: "B" },
    { text: "Powered adjustable base", type: "F" },
    { text: "Read or watch TV comfortably", type: "A" },
    { text: "Nights become your daily reward", type: "B" },
  ],
};

const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);

const FABGame = ({ lang, t }) => {
  const pool = FAB_POOL[lang];
  const [round, setRound] = useState(0);
  const [totalScore, setTotalScore] = useState(0);
  const [items, setItems] = useState(() => shuffle(pool).slice(0, 6));
  const [answers, setAnswers] = useState({});
  const [checked, setChecked] = useState(false);

  const setAnswer = (i, val) => setAnswers((a) => ({ ...a, [i]: val }));
  const correctCount = items.reduce((n, it, i) => n + (answers[i] === it.type ? 1 : 0), 0);

  const check = () => {
    setChecked(true);
    setTotalScore((s) => s + correctCount);
  };
  const nextRound = () => {
    setItems(shuffle(pool).slice(0, 6));
    setAnswers({});
    setChecked(false);
    setRound((r) => r + 1);
  };

  return (
    <div className="brutal p-6 bg-white" data-testid="game-fab">
      <GameHeader
        title={t("gameFabTitle")}
        subtitle={lang === "es" ? `Ronda ${round + 1}` : `Round ${round + 1}`}
        score={totalScore}
        total={(round + 1) * 6}
        testid="game-fab-score"
      />
      <p className="font-body text-xs text-neutral-600 mb-3">
        {lang === "es" ? "Marca F (Feature), A (Advantage) o B (Benefit)." : "Mark F (Feature), A (Advantage) or B (Benefit)."}
      </p>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={`${round}-${i}`} className="flex items-center justify-between gap-3 brutal-sm p-3 bg-canales-cream">
            <span className="font-body text-sm">{it.text}</span>
            <div className="flex gap-1 shrink-0">
              {["F", "A", "B"].map((k) => {
                const isPicked = answers[i] === k;
                const isRight = checked && it.type === k && isPicked;
                const isWrong = checked && isPicked && it.type !== k;
                return (
                  <button
                    key={k}
                    onClick={() => !checked && setAnswer(i, k)}
                    className={`w-9 h-9 rounded-full border-2 border-black font-display font-black ${
                      isRight ? "bg-green-500 text-white" : isWrong ? "bg-red-500 text-white" : isPicked ? "bg-canales-blue text-white" : "bg-white"
                    }`}
                  >
                    {k}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex gap-2 flex-wrap">
        <button onClick={check} className="brutal-btn bg-canales-red text-white px-4 py-2" data-testid={GAMES.submit}>
          {t("submit")}
        </button>
        <button
          onClick={() => { setAnswers({}); setChecked(false); }}
          className="brutal-btn bg-white px-4 py-2 flex items-center gap-2"
        >
          <RotateCcw className="w-4 h-4" /> Reset
        </button>
        <button
          onClick={nextRound}
          data-testid="game-fab-next-round"
          className="brutal-btn bg-canales-blue text-white px-4 py-2 flex items-center gap-2"
        >
          {lang === "es" ? "Otras 6" : "New 6"} <ArrowRight className="w-4 h-4" />
        </button>
      </div>
      <AnimatePresence>
        {checked && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 brutal p-4 bg-canales-yellow font-display font-bold flex items-center gap-2"
          >
            <Trophy className="w-5 h-5" /> {correctCount} / {items.length}
            {correctCount === items.length && (
              <span className="ml-2 inline-flex items-center gap-1 text-sm">
                <Sparkles className="w-4 h-4" />
                {lang === "es" ? "¡Ronda perfecta!" : "Perfect round!"}
              </span>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ============================================================
   GAME 3 — Find the mistake (5 scenarios)
============================================================ */
const MISTAKES = {
  es: [
    {
      title: "El saludo",
      lines: [
        "Cliente entra a la tienda.",
        "Vendedor no lo mira, sigue en el celular.",
        "Vendedor dice: '¿te puedo ayudar?'",
        "Cliente responde 'no' y se va.",
      ],
      mistakes: [1, 2],
    },
    {
      title: "El cierre",
      lines: [
        "Cliente prueba el sofá.",
        "Le encanta.",
        "Vendedor dice: 'piénselo y vuelva mañana'.",
        "Vendedor no ofrece financiamiento.",
      ],
      mistakes: [2, 3],
    },
    {
      title: "La queja",
      lines: [
        "Cliente reclama por un mueble rayado.",
        "Vendedor lo interrumpe: 'seguro fue el chofer'.",
        "Vendedor pide disculpas en nombre de Canales.",
        "Vendedor ofrece cambio inmediato y 15% en la próxima compra.",
      ],
      mistakes: [1],
    },
    {
      title: "El descubrimiento",
      lines: [
        "Vendedor dice: 'tenemos ofertas hoy'.",
        "Vendedor no pregunta para qué habitación es.",
        "Vendedor pregunta '¿cuántos son en la casa?'.",
        "Vendedor anota lo que dice el cliente.",
      ],
      mistakes: [0, 1],
    },
    {
      title: "Pitch de Tempur-Pedic",
      lines: [
        "Vendedor pregunta por el dolor de espalda.",
        "Vendedor solo habla de 'memory foam' y densidad.",
        "Vendedor no menciona la prueba de 90 noches.",
        "Vendedor cierra con opciones de entrega.",
      ],
      mistakes: [1, 2],
    },
  ],
  en: [
    {
      title: "The greeting",
      lines: [
        "Customer walks in.",
        "Seller doesn't look up, keeps scrolling phone.",
        "Seller says: 'can I help you?'",
        "Customer says 'no' and leaves.",
      ],
      mistakes: [1, 2],
    },
    {
      title: "The close",
      lines: [
        "Customer tries the sofa.",
        "Loves it.",
        "Seller says: 'think about it, come back tomorrow'.",
        "Seller doesn't mention financing.",
      ],
      mistakes: [2, 3],
    },
    {
      title: "The complaint",
      lines: [
        "Customer complains about a scratched piece.",
        "Seller interrupts: 'must have been the driver'.",
        "Seller apologizes on behalf of Canales.",
        "Seller offers immediate swap + 15% off next purchase.",
      ],
      mistakes: [1],
    },
    {
      title: "The discovery",
      lines: [
        "Seller says: 'we have deals today'.",
        "Seller doesn't ask which room it's for.",
        "Seller asks 'how many live at home?'.",
        "Seller notes what the customer says.",
      ],
      mistakes: [0, 1],
    },
    {
      title: "Tempur-Pedic pitch",
      lines: [
        "Seller asks about back pain.",
        "Seller only talks about 'memory foam' and density.",
        "Seller doesn't mention the 90-night trial.",
        "Seller closes with delivery options.",
      ],
      mistakes: [1, 2],
    },
  ],
};

const FindErrorGame = ({ lang, t }) => {
  const scenarios = MISTAKES[lang];
  const [scenIdx, setScenIdx] = useState(0);
  const [score, setScore] = useState(0);
  const s = scenarios[scenIdx];
  const [picked, setPicked] = useState([]);
  const [checked, setChecked] = useState(false);

  const toggle = (i) =>
    setPicked((p) => (p.includes(i) ? p.filter((x) => x !== i) : [...p, i]));

  const allRight =
    checked &&
    picked.length === s.mistakes.length &&
    s.mistakes.every((m) => picked.includes(m));

  const check = () => {
    setChecked(true);
    if (picked.length === s.mistakes.length && s.mistakes.every((m) => picked.includes(m))) {
      setScore((x) => x + 1);
    }
  };
  const nextScen = () => {
    setPicked([]);
    setChecked(false);
    setScenIdx((scenIdx + 1) % scenarios.length);
  };

  return (
    <div className="brutal p-6 bg-white" data-testid="game-error">
      <GameHeader
        title={t("gameErrorTitle")}
        subtitle={s.title}
        score={score}
        total={scenarios.length}
        testid="game-error-score"
      />
      <p className="font-body text-xs text-neutral-600 mb-3">
        {lang === "es" ? "Marca las líneas que tienen un error." : "Tap the lines that contain a mistake."}
      </p>
      <ul className="space-y-2">
        {s.lines.map((l, i) => {
          const isPicked = picked.includes(i);
          const isRight = checked && s.mistakes.includes(i) && isPicked;
          const isWrong = checked && isPicked && !s.mistakes.includes(i);
          const isMissed = checked && s.mistakes.includes(i) && !isPicked;
          return (
            <li key={i}>
              <button
                onClick={() => !checked && toggle(i)}
                className={`w-full text-left brutal-sm p-3 font-body ${
                  isRight ? "bg-green-200" : isWrong ? "bg-red-200" : isMissed ? "bg-yellow-100" : isPicked ? "bg-canales-yellow" : "bg-white"
                }`}
              >
                {i + 1}. {l}
              </button>
            </li>
          );
        })}
      </ul>
      <div className="mt-4 flex gap-2 flex-wrap">
        <button onClick={check} className="brutal-btn bg-canales-red text-white px-4 py-2">
          {t("submit")}
        </button>
        <button onClick={() => { setPicked([]); setChecked(false); }} className="brutal-btn bg-white px-4 py-2">
          Reset
        </button>
        <button
          onClick={nextScen}
          data-testid="game-error-next-scenario"
          className="brutal-btn bg-canales-blue text-white px-4 py-2 flex items-center gap-2"
        >
          {t("next")} <ArrowRight className="w-4 h-4" />
        </button>
      </div>
      <AnimatePresence>
        {checked && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mt-4 brutal p-4 font-display font-bold flex items-center gap-2 ${allRight ? "bg-green-100" : "bg-red-100"}`}
          >
            {allRight ? <Check className="text-green-700" /> : <X className="text-red-700" />}
            {allRight ? t("correct") : t("tryAgain")}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function Games() {
  const { t, lang } = useLang();
  const rules = useMemo(
    () => ({
      es: "Cada juego te da un punto por ronda perfecta. Practica hasta dominar los 3.",
      en: "Each game gives one point per perfect round. Practice until you master all 3.",
    }),
    []
  );

  return (
    <div className="min-h-screen bg-halftone">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 py-10" data-testid="games-root">
        <div className="mb-6 flex items-center gap-3">
          <Carlos size={64} waving />
          <div>
            <h1 className="font-display font-black text-4xl">{t("games")}</h1>
            <p className="font-body text-neutral-700">{rules[lang]}</p>
          </div>
          <div className="ml-auto hidden sm:block">
            <Ana size={64} />
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="grid md:grid-cols-2 gap-6"
        >
          <OrderGame lang={lang} t={t} />
          <FABGame lang={lang} t={t} />
          <div className="md:col-span-2">
            <FindErrorGame lang={lang} t={t} />
          </div>
        </motion.div>
      </div>
    </div>
  );
}
